import React from "react";

type SkeletonVariant = "table-row" | "stat-card" | "detail-card" | "text-block";

interface SkeletonLoaderProps {
  variant: SkeletonVariant;
  count?: number;
  columns?: number;
}

const shimmer = "animate-pulse bg-gray-200 rounded";

const TableRowSkeleton: React.FC<{ columns?: number }> = ({ columns = 6 }) => (
  <tr>
    {Array.from({ length: columns }).map((_, i) => (
      <td key={i} className="px-6 py-4">
        <div className={`h-4 ${shimmer} ${i === 0 ? "w-20" : i === columns - 1 ? "w-16" : "w-32"}`} />
      </td>
    ))}
  </tr>
);

const StatCardSkeleton: React.FC = () => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
    <div className="flex items-center justify-between">
      <div className="space-y-3 flex-1">
        <div className={`h-3 w-24 ${shimmer}`} />
        <div className={`h-8 w-16 ${shimmer}`} />
      </div>
      <div className={`h-12 w-12 rounded-lg ${shimmer}`} />
    </div>
  </div>
);

const DetailCardSkeleton: React.FC = () => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
    <div className={`h-5 w-40 ${shimmer}`} />
    <div className="grid grid-cols-2 gap-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="space-y-2">
          <div className={`h-3 w-20 ${shimmer}`} />
          <div className={`h-4 w-32 ${shimmer}`} />
        </div>
      ))}
    </div>
  </div>
);

const TextBlockSkeleton: React.FC = () => (
  <div className="space-y-2">
    <div className={`h-4 w-full ${shimmer}`} />
    <div className={`h-4 w-5/6 ${shimmer}`} />
    <div className={`h-4 w-4/6 ${shimmer}`} />
  </div>
);

const variantMap: Record<SkeletonVariant, React.FC<any>> = {
  "table-row": TableRowSkeleton,
  "stat-card": StatCardSkeleton,
  "detail-card": DetailCardSkeleton,
  "text-block": TextBlockSkeleton,
};

const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({
  variant,
  count = 1,
  columns,
}) => {
  const Component = variantMap[variant];

  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <Component key={i} columns={columns} />
      ))}
    </>
  );
};

export default SkeletonLoader;
